import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import {
  Search,
  Upload,
  Mail,
  Globe,
  Phone,
  Building2,
  Users,
  MapPin,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Square,
  Send,
  Download,
  RefreshCw,
  Trash2,
  Archive,
  Eye,
  History,
  Filter,
  FileSpreadsheet,
  X,
  Loader2,
} from "lucide-react";


// Network colors
const NETWORK_COLORS = {
  TWIG: "bg-blue-500",
  WFN: "bg-green-500",
  GAL: "bg-purple-500",
  UOL: "bg-orange-500",
  XLP: "bg-pink-500",
  NOATUM: "bg-cyan-500",
  COOP: "bg-amber-500",
  FLE: "bg-emerald-500",
  "FLE-D": "bg-teal-500",
  SEAJET: "bg-indigo-500",
  CONNECTA: "bg-yellow-500",
  PANGEA: "bg-sky-600",
};

const NETWORK_LABELS = {
  TWIG: "TWIG Network",
  WFN: "World Freight Network",
  GAL: "Global Alliance Corp",
  UOL: "United Ocean Lines",
  XLP: "XLP Network",
  NOATUM: "Noatum Logistics",
  COOP: "COOP Network",
  FLE: "Freightlinks (Origin)",
  "FLE-D": "Freightlinks (Destination)",
  SEAJET: "Seajet Logistics Network",
  CONNECTA: "Connecta Freight Network",
  PANGEA: "Pangea Network",
};

export default function PartnerDirectoryPage() {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [countries, setCountries] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Selection for bulk email
  const [selectedPartners, setSelectedPartners] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);

  // Email-CSV dialog (export selected networks to email as CSV attachment)
  const [emailCsvOpen, setEmailCsvOpen] = useState(false);
  const [emailCsvNetworks, setEmailCsvNetworks] = useState([]);
  const [emailCsvTo, setEmailCsvTo] = useState("");
  const [emailCsvSending, setEmailCsvSending] = useState(false);
  // id -> network map, accumulated across page loads + select-all, so the
  // breakdown reflects ALL selected partners (not just the current page).
  const [networkById, setNetworkById] = useState({});
  
  // Helper to get network breakdown of selected partners
  const getSelectedNetworks = () => {
    const networkCounts = {};
    selectedPartners.forEach((id) => {
      const net = networkById[id];
      if (net) networkCounts[net] = (networkCounts[net] || 0) + 1;
    });
    return networkCounts;
  };
  
  // Modals
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  
  // Email form
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [ccEmails, setCcEmails] = useState("");  // Comma-separated CC emails
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Attachments (drag-drop or file picker) — added 2026-05-27 by Rok's
  // request. Stored as objects with { name, size, type, content (base64) }.
  // 10 MB / file, 25 MB total enforced both UI-side AND on the backend.
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const MAX_FILE_MB = 10;
  const MAX_TOTAL_MB = 25;
  const _fileToBase64 = (file) => new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const dataUrl = fr.result || "";
      const comma = dataUrl.indexOf(",");
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(file);
  });
  const addEmailAttachments = async (filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    const tooBig = files.find(f => f.size > MAX_FILE_MB * 1024 * 1024);
    if (tooBig) {
      toast.error(`"${tooBig.name}" is larger than ${MAX_FILE_MB} MB.`);
      return;
    }
    const currentTotal = emailAttachments.reduce((s, a) => s + (a.size || 0), 0);
    const addedTotal = files.reduce((s, f) => s + f.size, 0);
    if ((currentTotal + addedTotal) > MAX_TOTAL_MB * 1024 * 1024) {
      toast.error(`Total attachments would exceed ${MAX_TOTAL_MB} MB. Remove some files first.`);
      return;
    }
    const newRows = [];
    for (const f of files) {
      try {
        const content = await _fileToBase64(f);
        newRows.push({
          name: f.name,
          size: f.size,
          type: f.type || "application/octet-stream",
          content,
        });
      } catch (e) {
        toast.error(`Failed to read "${f.name}"`);
      }
    }
    setEmailAttachments(prev => [...prev, ...newRows]);
  };
  const removeEmailAttachment = (idx) => {
    setEmailAttachments(prev => prev.filter((_, i) => i !== idx));
  };
  const onAttachmentsDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer?.files?.length) addEmailAttachments(e.dataTransfer.files);
  };

  // Import
  const [importNetwork, setImportNetwork] = useState("TWIG");
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // Logs
  const [emailLogs, setEmailLogs] = useState([]);

  const fetchPartners = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "50",
      });
      
      if (searchQuery) params.append("query", searchQuery);
      if (selectedNetwork) params.append("network", selectedNetwork);
      if (selectedCountry) params.append("country", selectedCountry);
      
      const res = await fetch(`${API_URL}/partner-directory/partners?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) throw new Error("Failed to fetch partners");
      
      const data = await res.json();
      setPartners(data.partners);
      setNetworkById((prev) => {
        const m = { ...prev };
        (data.partners || []).forEach((p) => { m[p.id] = p.network; });
        return m;
      });
      setTotalPages(data.pages);
      setTotal(data.total);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load partners");
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, selectedNetwork, selectedCountry]);

  const archivePartner = async (partner) => {
    if (!window.confirm(`Archive "${partner.company_name}"? It will be hidden from the directory, search and bulk email (you can restore it later).`)) return;
    try {
      const res = await fetch(`${API_URL}/partner-directory/partners/${partner.id}/archive`, {
        method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error();
      toast.success(`${partner.company_name} archived`);
      setSelectedPartners((prev) => { const n = new Set(prev); n.delete(partner.id); return n; });
      fetchPartners(); fetchStats();
    } catch { toast.error("Failed to archive partner"); }
  };

  const deletePartner = async (partner) => {
    if (!window.confirm(`Permanently DELETE "${partner.company_name}"? This cannot be undone. Use Archive if you just want to hide it.`)) return;
    try {
      const res = await fetch(`${API_URL}/partner-directory/partners/${partner.id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error();
      toast.success(`${partner.company_name} deleted`);
      setSelectedPartners((prev) => { const n = new Set(prev); n.delete(partner.id); return n; });
      fetchPartners(); fetchStats();
    } catch { toast.error("Failed to delete partner"); }
  };


  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/partner-directory/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCountries = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/partner-directory/countries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCountries(data.countries);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  useEffect(() => {
    fetchStats();
    fetchCountries();
  }, []);

  // Selection handlers
  const togglePartnerSelection = (partnerId) => {
    const newSelection = new Set(selectedPartners);
    if (newSelection.has(partnerId)) {
      newSelection.delete(partnerId);
    } else {
      newSelection.add(partnerId);
    }
    setSelectedPartners(newSelection);
  };

  // Select ALL partners matching the current filters (across EVERY page, not
  // just the 50 loaded on the current page).
  const toggleSelectAll = async () => {
    if (selectAll) {
      setSelectedPartners(new Set());
      setSelectAll(false);
      return;
    }
    try {
      setSelectingAll(true);
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ page: "1", limit: "100000" });
      if (searchQuery) params.append("query", searchQuery);
      if (selectedNetwork) params.append("network", selectedNetwork);
      if (selectedCountry) params.append("country", selectedCountry);
      const res = await fetch(`${API_URL}/partner-directory/partners?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to select all");
      const data = await res.json();
      setNetworkById((prev) => {
        const m = { ...prev };
        (data.partners || []).forEach((p) => { m[p.id] = p.network; });
        return m;
      });
      setSelectedPartners(new Set((data.partners || []).map((p) => p.id)));
      setSelectAll(true);
      toast.success(`Selected all ${data.total || (data.partners || []).length} partners`);
    } catch (e) {
      toast.error("Could not select all partners");
    } finally {
      setSelectingAll(false);
    }
  };

  const clearSelection = () => {
    setSelectedPartners(new Set());
    setSelectAll(false);
  };

  // Import handler
  const handleImport = async () => {
    if (!importFile) {
      toast.error("Please select a file");
      return;
    }

    try {
      setImporting(true);
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", importFile);

      const res = await fetch(`${API_URL}/partner-directory/import/${importNetwork}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Import failed");
      }

      const data = await res.json();
      toast.success(`Imported ${data.inserted} new partners, updated ${data.updated}`);
      setShowImportModal(false);
      setImportFile(null);
      fetchPartners();
      fetchStats();
      fetchCountries();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setImporting(false);
    }
  };

  // Bulk email handler
  const handleSendBulkEmail = async () => {
    if (selectedPartners.size === 0) {
      toast.error("Please select at least one partner");
      return;
    }
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error("Please fill in subject and body");
      return;
    }

    try {
      setSendingEmail(true);
      const token = localStorage.getItem("token");
      
      // Parse CC emails (comma or semicolon separated)
      const ccEmailList = ccEmails
        .split(/[,;]/)
        .map(e => e.trim())
        .filter(e => e.length > 0 && e.includes("@"));

      const res = await fetch(`${API_URL}/partner-directory/send-bulk-email`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          partner_ids: Array.from(selectedPartners),
          subject: emailSubject,
          body: emailBody,
          staff_email: user?.email || "",
          staff_name: user?.full_name || user?.email || "",
          cc_emails: ccEmailList,
          attachments: emailAttachments.map(a => ({
            filename: a.name,
            content: a.content,
            content_type: a.type,
          })),
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Failed to send email");
      }

      const data = await res.json();
      // Show detailed success message
      let successMsg = "";
      if (data.by_network && data.by_network.length > 1) {
        const networkSummary = data.by_network.map(n => `[${n.network}]: ${n.recipients_count}`).join(", ");
        successMsg = `Sent ${data.emails_sent} emails to ${data.total_recipients} recipients (${networkSummary})`;
      } else {
        successMsg = `Email sent to ${data.total_recipients} recipients`;
      }
      if (ccEmailList.length > 0) {
        successMsg += ` + ${ccEmailList.length} CC`;
      }
      toast.success(successMsg);
      setShowEmailModal(false);
      setEmailSubject("");
      setEmailBody("");
      setCcEmails("");
      setEmailAttachments([]);
      setSelectedPartners(new Set());
      setSelectAll(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingEmail(false);
    }
  };

  // Fetch email logs
  const fetchEmailLogs = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/partner-directory/email-logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setEmailLogs(data.logs);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Export CSV
  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (selectedNetwork) params.append("network", selectedNetwork);
      if (selectedCountry) params.append("country", selectedCountry);

      const res = await fetch(`${API_URL}/partner-directory/export?${params}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Export failed");

      const data = await res.json();
      
      // Download CSV
      const blob = new Blob([data.csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `partners_${selectedNetwork || "all"}_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      
      toast.success(`Exported ${data.count} partners`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Open the Email-CSV dialog, prefilling networks (current filter) + email.
  const openEmailCsv = () => {
    setEmailCsvNetworks(selectedNetwork ? [selectedNetwork] : []);
    setEmailCsvTo(user?.email || "");
    setEmailCsvOpen(true);
  };

  const toggleEmailCsvNetwork = (net) => {
    setEmailCsvNetworks((prev) =>
      prev.includes(net) ? prev.filter((n) => n !== net) : [...prev, net]
    );
  };

  // Email a combined CSV of the selected network(s) as an attachment.
  const handleEmailCsv = async () => {
    if (emailCsvNetworks.length === 0) { toast.error("Pick at least one network"); return; }
    if (!emailCsvTo || !emailCsvTo.includes("@")) { toast.error("Enter a valid email"); return; }
    setEmailCsvSending(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/partner-directory/export-email`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          networks: emailCsvNetworks,
          country: selectedCountry || null,
          email: emailCsvTo.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to send");
      toast.success(`Emailed ${data.count} partners (${data.networks.join(" + ")}) to ${data.sent_to}`);
      setEmailCsvOpen(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setEmailCsvSending(false);
    }
  };

  // Export XLS (Excel) - for easy forwarding via Outlook
  const handleExportXLS = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (selectedNetwork) params.append("network", selectedNetwork);
      if (selectedCountry) params.append("country", selectedCountry);
      if (searchQuery) params.append("query", searchQuery);

      const res = await fetch(`${API_URL}/partner-directory/export-xls?${params}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Export failed");

      // Get partner count from header
      const partnerCount = res.headers.get("X-Partner-Count") || "?";
      
      // Download XLS
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      
      // Generate filename based on filters
      let filename = "partners";
      if (selectedNetwork) filename += `_${selectedNetwork}`;
      if (selectedCountry) filename += `_${selectedCountry.replace(/\s+/g, "_")}`;
      if (searchQuery) filename += `_${searchQuery.replace(/\s+/g, "_").substring(0, 20)}`;
      filename += `_${new Date().toISOString().split("T")[0]}.xlsx`;
      
      a.download = filename;
      a.click();
      
      toast.success(`Exported ${partnerCount} partners to Excel`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetFilters = () => {
    setSearchQuery("");
    setSelectedNetwork("");
    setSelectedCountry("");
    setPage(1);
    setSelectedPartners(new Set());
    setSelectAll(false);
  };

  const getContactEmails = (partner) => {
    const emails = [];
    partner.contacts?.forEach((c) => {
      if (c.email) emails.push(c.email);
    });
    return emails;
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <div className="p-3 sm:p-4 lg:p-6 max-w-[1600px] mx-auto pt-16 sm:pt-6">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-4 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-white">Partner Directory</h1>
            <p className="text-slate-400 text-xs sm:text-sm">
              Manage logistics network partners and send bulk communications
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                fetchEmailLogs();
                setShowLogsModal(true);
              }}
              className="border-slate-700 text-xs sm:text-sm"
            >
              <History className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Email History</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              className="border-slate-700 text-xs sm:text-sm"
            >
              <Download className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Export CSV</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportXLS}
              className="border-slate-700 bg-green-900/30 hover:bg-green-800/50 text-xs sm:text-sm"
              title="Download as Excel file for easy forwarding via Outlook"
            >
              <FileSpreadsheet className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Export XLS</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={openEmailCsv}
              className="border-slate-700 bg-blue-900/30 hover:bg-blue-800/50 text-xs sm:text-sm"
              title="Email a CSV of selected network(s) to your inbox"
              data-testid="email-csv-btn"
            >
              <Mail className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Email CSV</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowImportModal(true)}
              className="border-slate-700 text-xs sm:text-sm"
            >
              <Upload className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Import</span>
            </Button>
            {selectedPartners.size > 0 && (
              <Button
                onClick={() => setShowEmailModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm"
              >
                <Mail className="h-4 w-4 mr-1" />
                Email ({selectedPartners.size})
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards - Horizontal scroll on mobile */}
        {stats && (
          <div className="flex gap-2 sm:gap-3 mb-4 sm:mb-6 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 scrollbar-hide sm:grid sm:grid-cols-4 lg:grid-cols-8">
            <Card className="bg-slate-900 border-slate-800 flex-shrink-0 w-24 sm:w-auto">
              <CardContent className="p-3 sm:p-4">
                <div className="text-lg sm:text-2xl font-bold text-white">{stats.total}</div>
                <div className="text-[10px] sm:text-xs text-slate-400">Total</div>
              </CardContent>
            </Card>
            {Object.entries(NETWORK_LABELS).map(([key, label]) => (
              <Card key={key} className="bg-slate-900 border-slate-800 flex-shrink-0 w-20 sm:w-auto">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <div className={`w-2 h-2 rounded-full ${NETWORK_COLORS[key]}`} />
                    <div className="text-base sm:text-xl font-bold text-white">
                      {stats.by_network?.[key] || 0}
                    </div>
                  </div>
                  <div className="text-[10px] sm:text-xs text-slate-400">{key}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <Card className="bg-slate-900 border-slate-800 mb-4 sm:mb-6">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col gap-3 sm:gap-4">
              {/* Search - Full width on mobile */}
              <div className="w-full">
                <Label className="text-slate-400 text-xs mb-1 block">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    placeholder="Company, country, email..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setPage(1);
                    }}
                    className="pl-9 bg-slate-800 border-slate-700 text-white text-sm"
                  />
                </div>
              </div>
              {/* Filters row - inline on mobile */}
              <div className="flex flex-wrap gap-2 sm:gap-4 items-end">
                <div className="w-[calc(50%-4px)] sm:w-36">
                  <Label className="text-slate-400 text-xs mb-1 block">Network</Label>
                  <Select value={selectedNetwork || "all"} onValueChange={(v) => { setSelectedNetwork(v === "all" ? "" : v); setPage(1); clearSelection(); }}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white text-sm">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Networks</SelectItem>
                      {Object.keys(NETWORK_LABELS).map((key) => (
                        <SelectItem key={key} value={key}>
                          {key}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-[calc(50%-4px)] sm:w-36">
                  <Label className="text-slate-400 text-xs mb-1 block">Country</Label>
                  <Select value={selectedCountry || "all"} onValueChange={(v) => { setSelectedCountry(v === "all" ? "" : v); setPage(1); clearSelection(); }}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white text-sm">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="all">All Countries</SelectItem>
                      {countries.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                    className="text-slate-400 px-2 sm:px-3"
                  >
                    <X className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Clear</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchPartners}
                    className="text-slate-400 px-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results count and select all */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3 sm:mb-4">
          <div className="text-xs sm:text-sm text-slate-400">
            Showing {partners.length} of {total} partners
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs sm:text-sm text-slate-400 cursor-pointer" data-testid="partner-select-all">
              <Checkbox
                checked={selectAll}
                disabled={selectingAll}
                onCheckedChange={toggleSelectAll}
              />
              {selectingAll ? "Selecting…" : `Select all${total ? ` (${total})` : ""}`}
            </label>
          </div>
        </div>

        {/* Partners Table - Horizontal scroll on mobile */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-0">
            <div className="overflow-x-auto -mx-3 sm:mx-0">
              <Table className="min-w-[600px]">
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-slate-900">
                    <TableHead className="w-10 text-slate-400"></TableHead>
                    <TableHead className="text-slate-400">Company</TableHead>
                    <TableHead className="text-slate-400">Network</TableHead>
                    <TableHead className="text-slate-400">Country</TableHead>
                    <TableHead className="text-slate-400">Contacts</TableHead>
                    <TableHead className="text-slate-400">Services</TableHead>
                    <TableHead className="text-slate-400 w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : partners.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                        No partners found. Import network files to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    partners.map((partner) => (
                      <TableRow
                        key={partner.id}
                        className="border-slate-800 hover:bg-slate-800/50 cursor-pointer"
                        onClick={() => {
                          setSelectedPartner(partner);
                          setShowDetailModal(true);
                        }}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedPartners.has(partner.id)}
                            onCheckedChange={() => togglePartnerSelection(partner.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-white">{partner.company_name}</div>
                          {partner.website && (
                            <a
                              href={partner.website.startsWith("http") ? partner.website : `https://${partner.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Globe className="h-3 w-3" />
                              {partner.website}
                            </a>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${NETWORK_COLORS[partner.network]} text-white`}>
                            {partner.network}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-300">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-slate-500" />
                            {partner.country}
                            {partner.city && `, ${partner.city}`}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {partner.contacts?.slice(0, 2).map((c, i) => (
                              <div key={i} className="text-xs">
                                {c.name && <span className="text-slate-300">{c.name}</span>}
                                {c.email && (
                                  <span className="text-blue-400 ml-1">
                                    {c.email}
                                  </span>
                                )}
                              </div>
                            ))}
                            {partner.contacts?.length > 2 && (
                              <div className="text-xs text-slate-500">
                                +{partner.contacts.length - 2} more
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {partner.services?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {partner.services.slice(0, 3).map((s, i) => (
                                <Badge key={i} variant="outline" className="text-xs border-slate-700 text-slate-400">
                                  {s}
                                </Badge>
                              ))}
                              {partner.services.length > 3 && (
                                <Badge variant="outline" className="text-xs border-slate-700 text-slate-400">
                                  +{partner.services.length - 3}
                                </Badge>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-0.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPartner(partner);
                                setShowDetailModal(true);
                              }}
                              title="View details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`partner-archive-${partner.id}`}
                              onClick={(e) => { e.stopPropagation(); archivePartner(partner); }}
                              title="Archive (hide from directory, search & bulk email)"
                              className="text-amber-500 hover:text-amber-600"
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`partner-delete-${partner.id}`}
                              onClick={(e) => { e.stopPropagation(); deletePartner(partner); }}
                              title="Delete permanently"
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="border-slate-700"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-slate-400 text-sm">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="border-slate-700"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Import Modal with Drag & Drop */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Import Partner List
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-400">Network</Label>
              <Select value={importNetwork} onValueChange={setImportNetwork}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(NETWORK_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {key} - {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Drag & Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                importFile 
                  ? 'border-green-500 bg-green-500/10' 
                  : 'border-slate-600 hover:border-slate-500 hover:bg-slate-800/50'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.add('border-blue-500', 'bg-blue-500/10');
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('border-blue-500', 'bg-blue-500/10');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('border-blue-500', 'bg-blue-500/10');
                const files = e.dataTransfer.files;
                if (files.length > 0) {
                  const file = files[0];
                  if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
                    setImportFile(file);
                  } else {
                    toast.error("Please drop an Excel file (.xls or .xlsx)");
                  }
                }
              }}
              onClick={() => document.getElementById('partner-import-file')?.click()}
            >
              <input
                id="partner-import-file"
                type="file"
                accept=".xls,.xlsx"
                onChange={(e) => setImportFile(e.target.files?.[0])}
                className="hidden"
              />
              {importFile ? (
                <div className="space-y-2">
                  <FileSpreadsheet className="h-12 w-12 mx-auto text-green-400" />
                  <p className="text-green-400 font-medium">{importFile.name}</p>
                  <p className="text-sm text-slate-400">
                    {(importFile.size / 1024).toFixed(1)} KB
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); setImportFile(null); }}
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="h-4 w-4 mr-1" /> Remove
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-12 w-12 mx-auto text-slate-500" />
                  <p className="text-slate-300">Drop your Excel file here</p>
                  <p className="text-sm text-slate-500">or click to browse</p>
                  <p className="text-xs text-slate-600">Supports .xls and .xlsx</p>
                </div>
              )}
            </div>
            
            <div className="text-xs text-slate-500 bg-slate-800/50 p-3 rounded">
              <strong>Supported formats:</strong>
              <ul className="mt-1 space-y-1">
                <li>• WFN - World Freight Network member list</li>
                <li>• XLP - Xpress Logistics Partners directory</li>
                <li>• UOL - United Ocean Lines agents</li>
                <li>• COOP - COOP Network directory</li>
                <li>• NOATUM - Noatum Logistics Network</li>
                <li>• TWIG - TWIG Logistics partner list</li>
                <li>• GAL - Global Alliance Corp</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImportModal(false); setImportFile(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={importing || !importFile}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Send Bulk Email (RFI/RFQ)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-slate-400">
              Sending to <span className="text-white font-medium">{selectedPartners.size} partners</span>
              {" "}via BCC. Replies will go to{" "}
              <span className="text-blue-400">{user?.email}</span>
            </div>
            
            {/* Network breakdown */}
            {Object.keys(getSelectedNetworks()).length > 0 && (
              <div className="bg-slate-800/50 p-3 rounded border border-slate-700">
                <div className="text-xs text-slate-400 mb-2">
                  <strong>Emails will be sent separately by network with [NETWORK] tag:</strong>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(getSelectedNetworks()).map(([network, count]) => (
                    <Badge key={network} className={`${NETWORK_COLORS[network]} text-white`}>
                      [{network}] → {count} partners
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            <div>
              <Label className="text-slate-400">Subject</Label>
              <div className="text-xs text-slate-500 mb-1">
                Network tag will be automatically added: [NETWORK] Your Subject
              </div>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="e.g., RFQ - Sea Freight from Shanghai to Hamburg"
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div>
              <Label className="text-slate-400">Message</Label>
              <Textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Dear Partner,

We are looking for rates for the following shipment..."
                rows={10}
                className="bg-slate-800 border-slate-700 mt-1 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-slate-400">CC (Optional)</Label>
              <div className="text-xs text-slate-500 mb-1">
                Add email addresses to CC (comma separated). Staff will receive a copy.
              </div>
              <Input
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                placeholder="colleague@europacific.com, manager@europacific.com"
                className="bg-slate-800 border-slate-700"
              />
            </div>

            {/* Attachments — drag-drop + file picker (added 2026-05-27).
                10 MB per file, 25 MB total. Limits mirrored on the backend. */}
            <div>
              <Label className="text-slate-400">Attachments (optional)</Label>
              <div className="text-xs text-slate-500 mb-1">
                Drag PDFs, images, or RFQ documents here (max 10 MB per file, 25 MB total). Same attachments go to every network.
              </div>
              <label
                htmlFor="partner-bulk-email-files"
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
                onDrop={onAttachmentsDrop}
                className={`block cursor-pointer rounded border-2 border-dashed p-4 text-center transition-colors ${
                  isDragOver
                    ? "border-blue-400 bg-blue-500/10"
                    : "border-slate-600 bg-slate-800/40 hover:border-slate-500"
                }`}
                data-testid="partner-bulk-attach-dropzone"
              >
                <input
                  id="partner-bulk-email-files"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => { addEmailAttachments(e.target.files); e.target.value = ""; }}
                  data-testid="partner-bulk-attach-input"
                />
                <div className="text-sm text-slate-300">
                  {isDragOver
                    ? "Release to attach"
                    : <>📎 <span className="underline">Click to choose files</span> or drag & drop them here</>
                  }
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  {emailAttachments.length > 0
                    ? `${emailAttachments.length} file${emailAttachments.length === 1 ? "" : "s"} attached`
                    : "No files attached yet"}
                </div>
              </label>
              {emailAttachments.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="partner-bulk-attach-list">
                  {emailAttachments.map((a, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs">
                      <span className="flex-1 truncate text-slate-200" title={a.name}>📄 {a.name}</span>
                      <span className="text-slate-500 shrink-0">{(a.size / 1024).toFixed(0)} KB</span>
                      <button
                        type="button"
                        onClick={() => removeEmailAttachment(idx)}
                        className="text-red-400 hover:text-red-300 shrink-0"
                        title="Remove"
                        data-testid={`partner-bulk-attach-remove-${idx}`}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <div className="text-[10px] text-slate-500 text-right">
                    Total: {(emailAttachments.reduce((s, a) => s + a.size, 0) / (1024 * 1024)).toFixed(2)} MB / {MAX_TOTAL_MB} MB
                  </div>
                </div>
              )}
            </div>

            <div className="text-xs text-slate-500 bg-slate-800 p-3 rounded">
              <strong>Email Details:</strong><br />
              From: roket@europacific.com<br />
              Reply-To: {user?.email} ({user?.full_name})<br />
              CC: {ccEmails || "(none)"}<br />
              Recipients: Partners grouped by network, each in BCC
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSendBulkEmail}
              disabled={sendingEmail}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Send className="h-4 w-4 mr-2" />
              {sendingEmail ? "Sending..." : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {selectedPartner?.company_name}
            </DialogTitle>
          </DialogHeader>
          {selectedPartner && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={`${NETWORK_COLORS[selectedPartner.network]} text-white`}>
                  {selectedPartner.network}
                </Badge>
                <span className="text-slate-400">
                  {NETWORK_LABELS[selectedPartner.network]}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-slate-500">Country</div>
                  <div className="text-white">{selectedPartner.country || "-"}</div>
                </div>
                <div>
                  <div className="text-slate-500">City</div>
                  <div className="text-white">{selectedPartner.city || "-"}</div>
                </div>
              </div>

              {selectedPartner.website && (
                <div>
                  <div className="text-slate-500 text-sm">Website</div>
                  <a
                    href={selectedPartner.website.startsWith("http") ? selectedPartner.website : `https://${selectedPartner.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline flex items-center gap-1"
                  >
                    <Globe className="h-4 w-4" />
                    {selectedPartner.website}
                  </a>
                </div>
              )}

              {selectedPartner.activities && (
                <div>
                  <div className="text-slate-500 text-sm">Activities</div>
                  <div className="text-slate-300 text-sm">{selectedPartner.activities}</div>
                </div>
              )}

              {selectedPartner.services?.length > 0 && (
                <div>
                  <div className="text-slate-500 text-sm mb-2">Services</div>
                  <div className="flex flex-wrap gap-1">
                    {selectedPartner.services.map((s, i) => (
                      <Badge key={i} variant="outline" className="border-slate-700 text-slate-300">
                        {s}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="text-slate-500 text-sm mb-2">Contacts</div>
                <div className="space-y-2">
                  {selectedPartner.contacts?.map((contact, i) => (
                    <div key={i} className="bg-slate-800 p-3 rounded text-sm">
                      {contact.name && (
                        <div className="font-medium text-white flex items-center gap-2">
                          <Users className="h-4 w-4 text-slate-500" />
                          {contact.name}
                          {contact.role && (
                            <span className="text-slate-500 text-xs">({contact.role})</span>
                          )}
                        </div>
                      )}
                      {contact.email && (
                        <div className="text-blue-400 flex items-center gap-2">
                          <Mail className="h-4 w-4 text-slate-500" />
                          <a href={`mailto:${contact.email}`} className="hover:underline">
                            {contact.email}
                          </a>
                        </div>
                      )}
                      {contact.phone && (
                        <div className="text-slate-300 flex items-center gap-2">
                          <Phone className="h-4 w-4 text-slate-500" />
                          {contact.phone}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                togglePartnerSelection(selectedPartner.id);
                setShowDetailModal(false);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {selectedPartners.has(selectedPartner?.id) ? "Deselect" : "Select for Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Logs Modal */}
      <Dialog open={showLogsModal} onOpenChange={setShowLogsModal}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Email History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {emailLogs.length === 0 ? (
              <div className="text-center text-slate-400 py-8">
                No emails sent yet
              </div>
            ) : (
              emailLogs.map((log, i) => (
                <div key={i} className="bg-slate-800 p-4 rounded">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-white">{log.subject}</div>
                    <Badge variant={log.status === "sent" ? "default" : "destructive"}>
                      {log.status}
                    </Badge>
                  </div>
                  <div className="text-sm text-slate-400 space-y-1">
                    <div>
                      Sent by: {log.staff_name} ({log.staff_email})
                    </div>
                    <div>
                      Recipients: {log.recipient_count} agents
                    </div>
                    <div>
                      Date: {new Date(log.sent_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogsModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email CSV dialog — export selected network(s) to email */}
      <Dialog open={emailCsvOpen} onOpenChange={setEmailCsvOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" /> Email partner CSV
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-400">Networks to include</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {Object.keys(NETWORK_LABELS).map((net) => (
                  <label key={net} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`email-csv-net-${net}`}>
                    <Checkbox
                      checked={emailCsvNetworks.includes(net)}
                      onCheckedChange={() => toggleEmailCsvNetwork(net)}
                    />
                    <span>{net} <span className="text-slate-500">({stats?.by_network?.[net] || 0})</span></span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Send to</Label>
              <Input
                type="email"
                value={emailCsvTo}
                onChange={(e) => setEmailCsvTo(e.target.value)}
                placeholder="you@europacific.com"
                className="mt-1 bg-slate-800 border-slate-700"
                data-testid="email-csv-to"
              />
              {selectedCountry && (
                <p className="text-[11px] text-slate-500 mt-1">Country filter applied: {selectedCountry}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-700" onClick={() => setEmailCsvOpen(false)}>Cancel</Button>
            <Button
              onClick={handleEmailCsv}
              disabled={emailCsvSending || emailCsvNetworks.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="email-csv-send"
            >
              {emailCsvSending ? "Sending…" : "Send CSV"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
